export type ParagraphDiffKind = "equal" | "added" | "removed" | "changed";

export type ParagraphDiffHunk = {
  id: string;
  kind: ParagraphDiffKind;
  /** Sentence-level (or short) diff units from the base manuscript. */
  baseParagraphs: string[];
  /** Sentence-level (or short) diff units from the variation. */
  variationParagraphs: string[];
  /** Original paragraph index for each base unit (groups sentences on merge). */
  baseUnitParagraphIndices: number[];
  /** Original paragraph index for each variation unit. */
  variationUnitParagraphIndices: number[];
  /** When true, the variation side wins for this hunk on merge. */
  useVariation: boolean;
  /**
   * Why this change was proposed (AI auto-fill or human editor note).
   * Optional — authors see it in the variation compare reason column.
   */
  reason?: string;
};

export type DiffSpan = {
  type: "equal" | "remove" | "add";
  text: string;
};

type DiffUnitWithParagraph = {
  text: string;
  paragraphIndex: number;
};

function normalizeParagraph(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function splitParagraphIntoSentences(paragraph: string): string[] {
  const trimmed = paragraph.trim();
  if (!trimmed) return [];

  const parts = trimmed
    .split(
      /(?<=[.!?…]["'”»]?)(?<!\b(?:Mr|Mrs|Ms|Dr|St|Sr|Jr|Prof|vs|etc|Vol|Ch|No|Miss|Capt|Lt|Sgt|Rep|Sen|Gen|Col|Maj|Rev|Hon|Dept|Est|Approx|Inc|Ltd|Co|Corp|Ave|Blvd|Rd|Mt|Ft|Jan|Feb|Mar|Aug|Sept|Oct|Nov|Dec)\.)\s+(?=[A-Z"“‘])/u
    )
    .map((part) => part.trim())
    .filter(Boolean);

  return parts.length ? parts : [trimmed];
}

function normalizeBlock(lines: string[]): string {
  return normalizeParagraph(lines.join(" "));
}

export function splitIntoParagraphs(text: string): string[] {
  const normalized = text.replace(/\r\n/g, "\n").trim();
  if (!normalized) return [];

  const byDoubleNewline = normalized
    .split(/\n{2,}/)
    .map(normalizeParagraph)
    .filter(Boolean);
  if (byDoubleNewline.length > 1) return byDoubleNewline;

  const bySingleNewline = normalized
    .split(/\n/)
    .map(normalizeParagraph)
    .filter(Boolean);
  if (bySingleNewline.length > 1) return bySingleNewline;

  return [normalizeParagraph(normalized)].filter(Boolean);
}

function splitIntoDiffUnitsWithParagraphs(text: string): DiffUnitWithParagraph[] {
  const paragraphs = splitIntoParagraphs(text);
  const units: DiffUnitWithParagraph[] = [];

  paragraphs.forEach((paragraph, paragraphIndex) => {
    const sentences = splitParagraphIntoSentences(paragraph);
    const parts = sentences.length ? sentences : [paragraph];
    for (const part of parts) {
      units.push({ text: part, paragraphIndex });
    }
  });

  return units;
}

/** Split prose into diff units (sentences when possible, otherwise whole paragraphs). */
export function splitIntoDiffUnits(text: string): string[] {
  return splitIntoDiffUnitsWithParagraphs(text).map((unit) => unit.text);
}

function defaultUseVariation(_kind: ParagraphDiffKind): boolean {
  return false;
}

/** Longest-common-subsequence table for string arrays. */
function buildLcsTable(base: string[], variation: string[]): number[][] {
  const rows = base.length + 1;
  const cols = variation.length + 1;
  const table = Array.from({ length: rows }, () => Array(cols).fill(0));
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      if (base[i - 1] === variation[j - 1]) {
        table[i][j] = table[i - 1][j - 1] + 1;
      } else {
        table[i][j] = Math.max(table[i - 1][j], table[i][j - 1]);
      }
    }
  }
  return table;
}

type TaggedDiffOp =
  | { type: "equal"; base: DiffUnitWithParagraph; variation: DiffUnitWithParagraph }
  | { type: "removed"; unit: DiffUnitWithParagraph }
  | { type: "added"; unit: DiffUnitWithParagraph };

function diffTaggedOps(base: DiffUnitWithParagraph[], variation: DiffUnitWithParagraph[]): TaggedDiffOp[] {
  const baseTexts = base.map((unit) => unit.text);
  const variationTexts = variation.map((unit) => unit.text);
  const table = buildLcsTable(baseTexts, variationTexts);
  const ops: TaggedDiffOp[] = [];
  let i = base.length;
  let j = variation.length;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && baseTexts[i - 1] === variationTexts[j - 1]) {
      ops.push({ type: "equal", base: base[i - 1], variation: variation[j - 1] });
      i -= 1;
      j -= 1;
    } else if (j > 0 && (i === 0 || table[i][j - 1] >= table[i - 1][j])) {
      ops.push({ type: "added", unit: variation[j - 1] });
      j -= 1;
    } else if (i > 0) {
      ops.push({ type: "removed", unit: base[i - 1] });
      i -= 1;
    }
  }
  ops.reverse();
  return ops;
}

type RawHunk = {
  kind: ParagraphDiffKind;
  base: DiffUnitWithParagraph[];
  variation: DiffUnitWithParagraph[];
};

function coalesceOps(ops: TaggedDiffOp[]): RawHunk[] {
  const hunks: RawHunk[] = [];
  let index = 0;
  while (index < ops.length) {
    const op = ops[index];
    if (op.type === "equal") {
      hunks.push({ kind: "equal", base: [op.base], variation: [op.variation] });
      index += 1;
      continue;
    }

    const removed: DiffUnitWithParagraph[] = [];
    const added: DiffUnitWithParagraph[] = [];
    while (index < ops.length && ops[index].type !== "equal") {
      const changeOp = ops[index];
      if (changeOp.type === "removed") removed.push(changeOp.unit);
      if (changeOp.type === "added") added.push(changeOp.unit);
      index += 1;
    }

    const removedTexts = removed.map((unit) => unit.text);
    const addedTexts = added.map((unit) => unit.text);

    if (removed.length && added.length) {
      if (normalizeBlock(removedTexts) === normalizeBlock(addedTexts)) {
        hunks.push({ kind: "equal", base: removed, variation: added });
      } else {
        hunks.push({ kind: "changed", base: removed, variation: added });
      }
    } else if (removed.length) {
      hunks.push({ kind: "removed", base: removed, variation: [] });
    } else if (added.length) {
      hunks.push({ kind: "added", base: [], variation: added });
    }
  }
  return hunks;
}

function mergeAdjacentEqualHunks(hunks: RawHunk[]): RawHunk[] {
  if (!hunks.length) return [];

  const merged: RawHunk[] = [];
  for (const hunk of hunks) {
    const previous = merged[merged.length - 1];
    if (previous && previous.kind === "equal" && hunk.kind === "equal") {
      previous.base.push(...hunk.base);
      previous.variation.push(...hunk.variation);
      continue;
    }
    merged.push({
      kind: hunk.kind,
      base: [...hunk.base],
      variation: [...hunk.variation],
    });
  }
  return merged;
}

function rawHunkToParagraphDiffHunk(hunk: RawHunk, index: number): ParagraphDiffHunk {
  return {
    id: `hunk-${index}`,
    kind: hunk.kind,
    baseParagraphs: hunk.base.map((unit) => unit.text),
    variationParagraphs: hunk.variation.map((unit) => unit.text),
    baseUnitParagraphIndices: hunk.base.map((unit) => unit.paragraphIndex),
    variationUnitParagraphIndices: hunk.variation.map((unit) => unit.paragraphIndex),
    useVariation: defaultUseVariation(hunk.kind),
  };
}

export function hunkHasMaterialChange(hunk: ParagraphDiffHunk): boolean {
  if (hunk.kind === "equal") return false;
  const baseNorm = normalizeBlock(hunk.baseParagraphs);
  const varNorm = normalizeBlock(hunk.variationParagraphs);
  if (hunk.kind === "removed") return baseNorm.length > 0;
  if (hunk.kind === "added") return varNorm.length > 0;
  return baseNorm !== varNorm;
}

export function diffChapterParagraphs(baseText: string, variationText: string): ParagraphDiffHunk[] {
  const base = splitIntoDiffUnitsWithParagraphs(baseText);
  const variation = splitIntoDiffUnitsWithParagraphs(variationText);
  const ops = diffTaggedOps(base, variation);
  const coalesced = mergeAdjacentEqualHunks(coalesceOps(ops));
  return coalesced.map(rawHunkToParagraphDiffHunk);
}

function tokenizeWords(text: string): string[] {
  return text.split(/\s+/).filter(Boolean);
}

type WordDiffOp =
  | { type: "equal"; text: string }
  | { type: "removed"; text: string }
  | { type: "added"; text: string };

function diffWordOps(baseWords: string[], variationWords: string[]): WordDiffOp[] {
  const table = buildLcsTable(baseWords, variationWords);
  const ops: WordDiffOp[] = [];
  let i = baseWords.length;
  let j = variationWords.length;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && baseWords[i - 1] === variationWords[j - 1]) {
      ops.push({ type: "equal", text: baseWords[i - 1] });
      i -= 1;
      j -= 1;
    } else if (j > 0 && (i === 0 || table[i][j - 1] >= table[i - 1][j])) {
      ops.push({ type: "added", text: variationWords[j - 1] });
      j -= 1;
    } else if (i > 0) {
      ops.push({ type: "removed", text: baseWords[i - 1] });
      i -= 1;
    }
  }
  ops.reverse();
  return ops;
}

function opsToSideSpans(ops: WordDiffOp[], side: "base" | "variation"): DiffSpan[] {
  const spans: DiffSpan[] = [];
  for (const op of ops) {
    if (op.type === "equal") {
      spans.push({ type: "equal", text: op.text });
      continue;
    }
    if (side === "base" && op.type === "removed") {
      spans.push({ type: "remove", text: op.text });
    }
    if (side === "variation" && op.type === "added") {
      spans.push({ type: "add", text: op.text });
    }
  }
  return spans;
}

/** Word-level highlights for a changed passage (git-style inline diff). */
export function diffWordSpans(baseText: string, variationText: string): { base: DiffSpan[]; variation: DiffSpan[] } {
  const baseWords = tokenizeWords(baseText);
  const variationWords = tokenizeWords(variationText);
  if (!baseWords.length && !variationWords.length) {
    return { base: [], variation: [] };
  }
  if (normalizeParagraph(baseText) === normalizeParagraph(variationText)) {
    return {
      base: baseText ? [{ type: "equal", text: baseText }] : [],
      variation: variationText ? [{ type: "equal", text: variationText }] : [],
    };
  }
  const ops = diffWordOps(baseWords, variationWords);
  return {
    base: opsToSideSpans(ops, "base"),
    variation: opsToSideSpans(ops, "variation"),
  };
}

function regroupUnitsIntoParagraphs(units: string[], paragraphIndices: number[]): string[] {
  if (!units.length) return [];

  const paragraphs: string[] = [];
  let groupParts = [units[0]];
  let groupIndex = paragraphIndices[0] ?? 0;

  for (let index = 1; index < units.length; index += 1) {
    const nextIndex = paragraphIndices[index] ?? groupIndex;
    if (nextIndex === groupIndex) {
      groupParts.push(units[index]);
      continue;
    }
    paragraphs.push(groupParts.join(" "));
    groupParts = [units[index]];
    groupIndex = nextIndex;
  }

  paragraphs.push(groupParts.join(" "));
  return paragraphs.map(normalizeParagraph).filter(Boolean);
}

function selectedUnitsForHunk(hunk: ParagraphDiffHunk): { units: string[]; indices: number[] } {
  if (hunk.kind === "equal") {
    return {
      units: hunk.baseParagraphs,
      indices: hunk.baseUnitParagraphIndices,
    };
  }
  if (hunk.kind === "changed") {
    return hunk.useVariation
      ? { units: hunk.variationParagraphs, indices: hunk.variationUnitParagraphIndices }
      : { units: hunk.baseParagraphs, indices: hunk.baseUnitParagraphIndices };
  }
  if (hunk.kind === "added") {
    return hunk.useVariation
      ? { units: hunk.variationParagraphs, indices: hunk.variationUnitParagraphIndices }
      : { units: [], indices: [] };
  }
  if (hunk.kind === "removed") {
    return hunk.useVariation
      ? { units: [], indices: [] }
      : { units: hunk.baseParagraphs, indices: hunk.baseUnitParagraphIndices };
  }
  return { units: hunk.baseParagraphs, indices: hunk.baseUnitParagraphIndices };
}

export function previewParagraphsForHunk(hunk: ParagraphDiffHunk): string[] {
  const { units, indices } = selectedUnitsForHunk(hunk);
  return regroupUnitsIntoParagraphs(units, indices);
}

export function mergeSelectedHunks(hunks: ParagraphDiffHunk[]): string {
  const paragraphs: string[] = [];
  for (const hunk of hunks) {
    paragraphs.push(...previewParagraphsForHunk(hunk));
  }
  return paragraphs.join("\n\n");
}

export function countChangedHunks(hunks: ParagraphDiffHunk[]): number {
  return hunks.filter((hunk) => hunkHasMaterialChange(hunk)).length;
}

export function countAppliedHunks(hunks: ParagraphDiffHunk[]): number {
  return hunks.filter((hunk) => hunkHasMaterialChange(hunk) && hunk.useVariation).length;
}

export function hunkSummary(hunk: ParagraphDiffHunk): string {
  if (!hunkHasMaterialChange(hunk)) return "Unchanged";
  if (hunk.kind === "removed") return "Delete from manuscript";
  if (hunk.kind === "added") return "Insert new passage";
  return "Replace passage";
}

export function hunkVariationText(hunk: ParagraphDiffHunk): string {
  return hunk.variationParagraphs.join("\n\n");
}

export function applyHunkVariationText(hunk: ParagraphDiffHunk, text: string): ParagraphDiffHunk {
  if (hunk.kind === "removed") return hunk;

  const trimmed = text.trim();
  const paragraphs = trimmed.includes("\n\n")
    ? trimmed
        .split(/\n{2,}/)
        .map(normalizeParagraph)
        .filter(Boolean)
    : trimmed
      ? [normalizeParagraph(trimmed)]
      : [];

  const fallbackIndex =
    hunk.variationUnitParagraphIndices[0] ?? hunk.baseUnitParagraphIndices[0] ?? 0;
  const indices = paragraphs.map(
    (_, index) => hunk.variationUnitParagraphIndices[index] ?? fallbackIndex
  );

  return {
    ...hunk,
    variationParagraphs: paragraphs,
    variationUnitParagraphIndices: indices,
  };
}

/** Rebuild the full variation document from diff hunks (variation side wins for each block). */
export function rebuildVariationContentFromHunks(hunks: ParagraphDiffHunk[]): string {
  return mergeSelectedHunks(hunks.map((hunk) => ({ ...hunk, useVariation: true })));
}

export function findMatchingOriginalHunk(
  hunk: ParagraphDiffHunk,
  originals: ParagraphDiffHunk[]
): ParagraphDiffHunk | null {
  const byId = originals.find((candidate) => candidate.id === hunk.id);
  if (byId) return byId;

  const baseKey = normalizeBlock(hunk.baseParagraphs);
  if (!baseKey) return null;
  return (
    originals.find(
      (candidate) =>
        candidate.kind !== "equal" && normalizeBlock(candidate.baseParagraphs) === baseKey
    ) ?? null
  );
}

export function hunkVariationDiffersFromOriginal(
  hunk: ParagraphDiffHunk,
  original: ParagraphDiffHunk | null
): boolean {
  if (!original || hunk.kind === "removed") return false;
  return normalizeBlock(hunk.variationParagraphs) !== normalizeBlock(original.variationParagraphs);
}

export function preserveHunkPreviewState(
  nextHunks: ParagraphDiffHunk[],
  previousHunks: ParagraphDiffHunk[]
): ParagraphDiffHunk[] {
  return nextHunks.map((hunk) => {
    const previous =
      previousHunks.find((candidate) => candidate.id === hunk.id) ??
      findMatchingOriginalHunk(hunk, previousHunks);
    if (!previous) return hunk;
    return {
      ...hunk,
      useVariation: previous.useVariation,
      reason: previous.reason ?? hunk.reason,
    };
  });
}
